import React, { useCallback, useContext, useState, useMemo, useEffect } from 'react';
import { SelectableCardList, getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { withDefaultStyles, useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import classNames from 'classnames';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Label, Checkbox, Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton } from '@fluentui/react';
import { withScope } from '@bingads-webui-react/scope-service';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { ClarityGraphInstruction } from '@bingads-webui-campaign-react/clarity-graph-instruction';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';
import { StepContainer } from '../step-container/step-container';
import { WIZARD_ACTIONS } from '../../goal-creation-wizard-reducer';
import { enableClarityForTag, getErrorMessage, saveGoal, processPayload } from '../../conversion-goals-repository';

const UnstyledTagSetupChoiceSelectionStep = ({ classes, scope, serverContext }) => {
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const { logActivity } = wizardContext;
  const { Tag, tags } = wizardContext.goalContext;
  const tagObject = _.find(tags, tag => Tag.Id === tag.Id);
  const isClarityEnabled = !!(Tag.IsClarityTagEnabled || tagObject.IsClarityTagEnabled);
  const [clarityConsentReceived, setClarityConsentRecieved] = useState(isClarityEnabled);
  const [notification, setNotification] = useState(null);
  const [showBlocker, setShowBlocker] = useState(false);
  const [hideSwitchToManualDialog, setHideSwitchToManualDialog] = useState(true);

  const cardStyles = useDefaultStyles(() => ({
    footer: {
      color: 'black',
      fontWeight: 600,
      fontSize: '12px',
      width: 'fit-content',
      background: '#DFF6DD',
      padding: '2px 8px',
    },
    selectableButtonLarge: {
      height: 187,
    },
  }));

  useEffect(() => {
    let notificationId;
    if (!_.isEmpty(notification)) {
      if (notification.message) {
        notificationId = notificationApi.showInfo(notification.message);
      } else if (notification.error) {
        notificationId = notificationApi.showWarning(notification.error);
      }
    }
    return () => notificationId && notificationApi.dismiss(notificationId);
  }, [notification]);

  const tagSetupChoices = useMemo(() => ([
    {
      id: 'clarity',
      title: i18n.getString(_TL_(('Code-free, powered by Microsoft Clarity'))),
      description: i18n.getString(_TL_(('Navigate to your website and select a button or link. This will become your ad conversion goal. No code required.'))),
      iconName: 'Link',
      footer: i18n.getString(_TL_('Recommended')),
    },
    {
      id: 'manualSetup',
      title: i18n.getString(_TL_(('Manual installation'))),
      description: i18n.getString(_TL_(('Follow the detailed instructions to install your event code manually. You can also email the instruction to your developer.'))),
      iconName: 'Link',
    },
  ]), [i18n]);

  const [eventTagSetupMethod, setEventTagSetupMethod] = useState('clarity');

  const onSelectTagSetupChoice = useCallback((newChoice) => {
    setEventTagSetupMethod(newChoice);
  }, []);

  const handleNext = useCallback(() => {
    if (eventTagSetupMethod === 'clarity') {
      logActivity.trace(`[TagSetupChoice] Clarity setup was chosed for tag Id: ${Tag.Id}`);
    }
    if (!isClarityEnabled && clarityConsentReceived) {
      logActivity.trace(`[TagSetupChoice] Enabling Clarity for tag Id: ${Tag.Id}`);
      setShowBlocker(true);
      enableClarityForTag(scope, serverContext, tagObject).then(() => {
        setShowBlocker(false);
        wizardContext.dispatch({
          type: WIZARD_ACTIONS.EVENT_TAG_SETUP,
          eventTagSetupMethod,
          enableClarity: true,
          Tag: {
            ...Tag,
            IsClarityTagEnabled: 1,
          },
        });
      // eslint-disable-next-line no-unused-vars
      }).catch((_e) => {
        setShowBlocker(false);
        setNotification({ error: i18n.getString(_TL_('We could not provision Clarity for this tag. Please select a different tag or use manual setup.')) });
      });
    } else if (eventTagSetupMethod === 'manualSetup' && wizardContext.goalContext.ClarityEventDefinitionId) {
      setHideSwitchToManualDialog(false);
    } else {
      wizardContext.dispatch({
        type: WIZARD_ACTIONS.EVENT_TAG_SETUP,
        eventTagSetupMethod,
      });
    }
  }, [eventTagSetupMethod, isClarityEnabled, clarityConsentReceived, wizardContext, logActivity, Tag, scope, serverContext, tagObject, i18n]);

  const enableNextButton = eventTagSetupMethod !== 'clarity' || clarityConsentReceived;

  const footerConfig = useMemo(() => ({
    buttons: [{
      type: 'Secondary',
      onClick: wizardContext.goBack,
      isEnabled: true,
      text: i18n.getString(_TL_(('Back'))),
    },
    {
      type: 'Primary',
      onClick: handleNext,
      isEnabled: enableNextButton,
      text: i18n.getString(_TL_(('Next'))),
    },
    {
      type: 'Cancel',
      onClick: wizardContext.cancel,
      isEnabled: true,
      text: i18n.getString(_TL_(('Cancel'))),
    }],
  }), [enableNextButton, handleNext, i18n, wizardContext.cancel, wizardContext.goBack]);

  const onRenderClarityLabel = () => (
    <ClarityGraphInstruction i18n={i18n} />
  );

  const onChangeClarityStatus = useCallback((event, checked) => {
    const isEnabled = !!checked;
    setClarityConsentRecieved(isEnabled);
  }, [setClarityConsentRecieved]);

  const onSwitchToManualDismiss = useCallback(() => {
    setHideSwitchToManualDialog(true);
  }, []);

  const onSwitchToManualConfirm = useCallback(() => {
    // delete clarity association
    const payload = processPayload(wizardContext.goalContext, wizardContext.permissions, true);
    saveGoal(payload, wizardContext).then(() => {
      wizardContext.dispatch({
        type: WIZARD_ACTIONS.EVENT_TAG_SETUP,
        data: payload,
        eventTagSetupMethod,
      });
    })
      .catch((response) => {
        setNotification({ error: getErrorMessage(response, i18n) });
      });
  }, [eventTagSetupMethod, i18n, wizardContext]);

  const dialogContentProps = {
    title: i18n.getString(_TL_('Are you sure you want to switch to manual installation?')),
    type: DialogType.normal,
  };

  return (
    <UiBlocker blocking={showBlocker} loadingText={i18n.getString(_TL_('Loading...'))}>
      <StepContainer
        subContainer
        step="tag-setup-choice"
        footerConfig={footerConfig}
        title={i18n.getString(_TL_('Install event tag'))}
      >
        <div className={classes.contentBlock}>
          <Label>{i18n.getString(_TL_('How would you like to install your event tag?'))}</Label>
          <SelectableCardList
            contents={tagSetupChoices}
            classes={cardStyles}
            selectedItem={eventTagSetupMethod}
            onItemSelected={onSelectTagSetupChoice}
            useLargeCards
          />
        </div>
        <div className={classes.contentBlock}>
          {eventTagSetupMethod === 'clarity' && !isClarityEnabled &&
          <>
            <Label>{i18n.getString(_TL_('Enable Microsoft Clarity'))}</Label>
            <Checkbox
              className={classNames(classes.section, 'clarity-checkbox')}
              label={i18n.getString(_TL_('Microsoft Clarity brings advertisers actionable user behavioral insights to optimize their ad campaigns. By enabling Clarity tracking, you agree to the Clarity terms of use and privacy policy. Learn more'))}
              onRenderLabel={onRenderClarityLabel}
              checked={clarityConsentReceived}
              onChange={onChangeClarityStatus}
            />
            <div className="alert alert-info">
              <span className="alert-image" />
              <span>
                {i18n.getString(_TL_('To continue with the Code-free, powered by Clarity option, you need to enable Microsoft Clarity for the selected UET tag.'))}
              </span>
            </div>
          </>}
        </div>
      </StepContainer>
      <Dialog
        hidden={hideSwitchToManualDialog}
        onDismiss={onSwitchToManualDismiss}
        dialogContentProps={dialogContentProps}
      >
        <div>
          <p>{i18n.getString(_TL_('Switching to manual installation will delete and reset the existing goal parameters.'))}
          </p>
        </div>
        <DialogFooter>
          <PrimaryButton onClick={onSwitchToManualConfirm} className="confirm-switch-to-manual-button" text={i18n.getString(_TL_('Switch anyway'))} />
          <DefaultButton onClick={onSwitchToManualDismiss} className="cancel-switch-to-manual-button" text={i18n.getString(_TL_('Cancel'))} />
        </DialogFooter>
      </Dialog>
    </UiBlocker>
  );
};

UnstyledTagSetupChoiceSelectionStep.propTypes = {
  classes: PropTypes.shape({
    contentBlock: PropTypes.string.isRequired,
    section: PropTypes.string.isRequired,
  }).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({}).isRequired,
};

export const TagSetupChoiceSelectionStep = withServerContext(withScope(withDefaultStyles(UnstyledTagSetupChoiceSelectionStep, getStepStyles)));
